<script setup lang="ts">
import { computed } from 'vue'
import { FairIcon } from '@/ui/primitives/FairIcon'
import type { FairIconName } from '@/ui/tokens/icons.data'
import {
  FAIR_BUTTON_ICON_SIZE,
  type FairButtonPreviewState,
  type FairButtonTheme,
  type FairButtonVariant,
} from '@/ui/tokens/button.data'
import { FAIR_BUTTON_DS_SIZES, type FairButtonSize } from '@/ui/tokens/types'

interface FairButtonProps {
  label?: string
  variant?: FairButtonVariant
  size?: FairButtonSize
  rounded?: boolean
  iconOnly?: boolean
  link?: boolean
  theme?: FairButtonTheme
  leadingIcon?: FairIconName
  trailingIcon?: FairIconName
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  ariaLabel?: string
  previewState?: FairButtonPreviewState
  interactive?: boolean
  /** DS Loading state — label and icons replaced by the loader (buttons only) */
  loading?: boolean
}

const props = withDefaults(defineProps<FairButtonProps>(), {
  label: 'Для себя',
  variant: 'primary',
  size: 'l',
  rounded: true,
  iconOnly: false,
  link: false,
  theme: 'light',
  leadingIcon: undefined,
  trailingIcon: undefined,
  disabled: false,
  type: 'button',
  ariaLabel: undefined,
  previewState: undefined,
  interactive: true,
  loading: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const isDisabled = computed(() =>
  props.disabled || props.previewState === 'disabled',
)

/** DS sizes render the Figma Button (radius/control, DS states); `l` / `m` keep the v1 look. */
const isDs = computed(() => (FAIR_BUTTON_DS_SIZES as readonly string[]).includes(props.size))

const showLeadingIcon = computed(() =>
  Boolean(props.leadingIcon) && (!props.iconOnly || !props.trailingIcon),
)

const showTrailingIcon = computed(() => Boolean(props.trailingIcon))

const iconOnlyName = computed(() =>
  props.leadingIcon ?? props.trailingIcon,
)

const iconSize = computed(() => FAIR_BUTTON_ICON_SIZE[props.size])

const rootClass = computed(() => ({
  'fair-button': true,
  'type-button': true,
  [`fair-button--${props.variant}`]: true,
  [`fair-button--size-${props.size}`]: true,
  'fair-button--ds': isDs.value,
  'fair-button--loading': props.loading,
  'fair-button--rounded': props.rounded && !isDs.value,
  'fair-button--square': !props.rounded && !isDs.value,
  'fair-button--icon-only': props.iconOnly,
  'fair-button--link': props.link && props.variant === 'flat',
  [`fair-button--theme-${props.theme}`]: true,
  'fair-button--disabled': isDisabled.value,
  'fair-button--non-interactive': !props.interactive,
  [`fair-button--preview-${props.previewState}`]: Boolean(props.previewState),
}))

const iconColor = computed(() => {
  if (isDs.value) return 'currentColor'
  if (props.variant === 'primary' || props.variant === 'secondary') {
    return 'var(--color-white)'
  }

  if (props.variant === 'gradient' || props.variant === 'flat-action') {
    return 'var(--color-text-primary)'
  }

  if (props.variant === 'flat') {
    return props.link
      ? 'var(--color-text-primary)'
      : 'var(--color-text-primary)'
  }

  if (props.variant === 'outlined-action') {
    return 'var(--color-text-primary)'
  }

  if (
    props.variant === 'tonal'
    || props.variant === 'tonal-action'
    || props.variant === 'glass'
    || props.variant === 'outlined'
  ) {
    if (props.theme === 'dark') {
      return 'var(--color-white)'
    }

    return 'var(--color-text-primary)'
  }

  return 'currentColor'
})

function handleClick(event: MouseEvent) {
  if (!props.interactive || isDisabled.value || props.loading) {
    return
  }

  emit('click', event)
}
</script>

<template>
  <button
    :type="type"
    :class="rootClass"
    :disabled="isDisabled"
    :aria-label="ariaLabel ?? (iconOnly ? label : undefined)"
    :aria-busy="loading || undefined"
    @click="handleClick"
  >
    <span
      v-if="loading"
      class="fair-button__loader"
      aria-hidden="true"
    />
    <span
      v-else-if="iconOnly && iconOnlyName"
      class="fair-button__icon"
    >
      <FairIcon
        :name="iconOnlyName"
        :size="iconSize"
        :color="iconColor"
      />
    </span>

    <template v-else>
      <span
        v-if="showLeadingIcon && leadingIcon"
        class="fair-button__icon"
      >
        <FairIcon
          :name="leadingIcon"
          :size="iconSize"
          :color="iconColor"
        />
      </span>

      <span
        v-if="!iconOnly"
        class="fair-button__label"
      >
        <slot>{{ label }}</slot>
      </span>

      <span
        v-if="showTrailingIcon && trailingIcon"
        class="fair-button__icon"
      >
        <FairIcon
          :name="trailingIcon"
          :size="iconSize"
          :color="iconColor"
        />
      </span>
    </template>
  </button>
</template>
